// AdvancedModeContext — exposes the Advanced Mode preference to every page.
//
// Responsibilities:
//   1. Read/write `advanced_mode_enabled` from localStorage prefs (keyed by
//      the current session's alias).
//   2. Register a global `Cmd/Ctrl + Shift + A` keyboard shortcut that
//      toggles the preference.
//   3. Announce state changes via an `aria-live="polite"` region so screen
//      reader users hear "Advanced mode on" / "Advanced mode off".
//
// Advanced Mode is NOT an authorization boundary — it only reveals raw IDs
// and JSON fields. See requirement 5.4.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSession } from "@/session";
import { loadPrefs, savePrefs } from "@/utils/prefs";

export interface AdvancedModeContextValue {
  /** Whether Advanced Mode is currently enabled. */
  isAdvanced: boolean;
  /** Flip Advanced Mode on/off. Persists to localStorage. */
  toggle: () => void;
  /** Directly set Advanced Mode state (used by programmatic callers). */
  setAdvanced: (next: boolean) => void;
  /** True while the initial prefs load is pending. */
  loading: boolean;
}

const AdvancedModeContext = createContext<AdvancedModeContextValue | undefined>(
  undefined,
);

/** Anonymous fallback key used when no authenticated alias is available yet. */
const ANON_ALIAS = "anon";

/**
 * Detect the Advanced Mode keyboard shortcut: Cmd+Shift+A on macOS,
 * Ctrl+Shift+A elsewhere. Returns true when the event matches.
 */
function isToggleShortcut(e: KeyboardEvent): boolean {
  // `e.key` is case-sensitive on Shift, so compare lowercased.
  if (e.key.toLowerCase() !== "a") return false;
  if (!e.shiftKey) return false;
  // metaKey covers Cmd on macOS; ctrlKey covers Ctrl elsewhere. Accepting
  // both on all platforms is forgiving and matches the task spec
  // ("Cmd/Ctrl+Shift+A").
  return e.metaKey || e.ctrlKey;
}

/**
 * Provider for Advanced Mode state. Must be rendered inside SessionProvider
 * so the alias is available for prefs keying.
 */
export function AdvancedModeProvider({ children }: { children: ReactNode }): JSX.Element {
  const { session } = useSession();
  const alias = session?.alias ?? ANON_ALIAS;

  const [isAdvanced, setIsAdvanced] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [announcement, setAnnouncement] = useState<string>("");

  // Remember which alias's prefs we've loaded so a silent re-render doesn't
  // repeatedly stomp on setIsAdvanced after a user toggle.
  const loadedAliasRef = useRef<string | null>(null);

  // Load prefs when the alias changes (sign-in, role switch that changes
  // session identity, etc.). The dependency on `alias` is intentional.
  useEffect(() => {
    if (loadedAliasRef.current === alias) return;
    loadedAliasRef.current = alias;
    const prefs = loadPrefs(alias);
    setIsAdvanced(prefs.advanced_mode_enabled);
    setLoading(false);
  }, [alias]);

  const persist = useCallback(
    (next: boolean) => {
      savePrefs(alias, { advanced_mode_enabled: next });
    },
    [alias],
  );

  const setAdvanced = useCallback(
    (next: boolean): void => {
      setIsAdvanced((prev) => {
        if (prev === next) return prev;
        persist(next);
        setAnnouncement(next ? "Advanced mode on" : "Advanced mode off");
        return next;
      });
    },
    [persist],
  );

  const toggle = useCallback((): void => {
    setIsAdvanced((prev) => {
      const next = !prev;
      persist(next);
      setAnnouncement(next ? "Advanced mode on" : "Advanced mode off");
      return next;
    });
  }, [persist]);

  // Keyboard shortcut: Cmd/Ctrl + Shift + A anywhere toggles the mode.
  // We listen on `window` with capture=false and avoid swallowing the event
  // when focus is in an editable field where the combo might collide with
  // browser defaults — but there is no standard Ctrl+Shift+A browser action,
  // so capture is safe.
  useEffect(() => {
    function handler(e: KeyboardEvent): void {
      if (!isToggleShortcut(e)) return;
      e.preventDefault();
      toggle();
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggle]);

  const value = useMemo<AdvancedModeContextValue>(
    () => ({ isAdvanced, toggle, setAdvanced, loading }),
    [isAdvanced, toggle, setAdvanced, loading],
  );

  return (
    <AdvancedModeContext.Provider value={value}>
      {children}
      {/* aria-live region — visually hidden but announced by screen readers
          when `announcement` updates. Kept at the provider level so every
          app surface benefits without each page mounting its own region. */}
      <span
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {announcement}
      </span>
    </AdvancedModeContext.Provider>
  );
}

/** Hook for consuming Advanced Mode state. */
// eslint-disable-next-line react-refresh/only-export-components
export function useAdvancedMode(): AdvancedModeContextValue {
  const ctx = useContext(AdvancedModeContext);
  if (!ctx) {
    throw new Error("useAdvancedMode must be used inside <AdvancedModeProvider>");
  }
  return ctx;
}
